import assert from 'assert-ts';
import type {Part, Schema} from 'schemaDefinition/types';
import type {Column} from 'services/advancedSearch/types';

export const mapDataColumnsToSchema = (
  schemaName: string,
  allColumns: Column[],
  currentColumns: string[],
): Schema => {
  // Exclude any columns that are not in allColumns, probably deprecated/removed from backend.
  const existingColumns = currentColumns.filter(cc =>
    allColumns.find(ac => ac.name === cc),
  );

  return {
    name: schemaName,
    parts: existingColumns.map(cc => {
      const currentColumn = allColumns.find(ac => ac.name === cc);

      return mapColumnPart(
        assert(
          currentColumn,
          `mapDataColumnsToSchema: Unexpected column: ${cc}`,
          {cc},
        ),
      );
    }),
  };
};

const mapColumnPart = (column: Column): Part => {
  /**
   * MUI DataGrid does not support array of dates as a date.
   */
  const isMultipleDate = [
    'salesRestrictionStartDate',
    'licenseFromDate',
    'licenseUntilDate',
  ].includes(column.name);

  if (isMultipleDate) {
    return {
      name: column.name,
      type: 'text', // Multiple dates cannot be passed to MUI DataGrid as date.
      cardinality: 'multiple',
    };
  }

  switch (column.type) {
    case 'TEXT':
      if (column.codelistId) {
        return {
          type: 'codelist',
          codelistId: column.codelistId,
          name: column.name,
        };
      }

      if (column.name === 'themas') {
        return {
          name: column.name,
          type: 'thesaurus',
          thesaurusId: 'thema',
          cardinality: 'multiple',
        };
      }

      if (column.name === 'themasCodes') {
        return {
          name: column.name,
          type: 'thesaurus',
          thesaurusId: 'thema',
          cardinality: 'multiple',
          showCode: 'onlyCode',
        };
      }

      if (column.name === 'subjects') {
        return {
          name: column.name,
          type: 'thesaurus',
          thesaurusId: 'bokbasen',
          cardinality: 'multiple',
        };
      }

      if (column.name === 'norwegianSubjects') {
        return {
          name: column.name,
          type: 'thesaurus',
          thesaurusId: 'neo',
          cardinality: 'multiple',
        };
      }

      if (column.name === 'genreAndForm') {
        return {
          name: column.name,
          type: 'thesaurus',
          thesaurusId: 'genreandform',
          cardinality: 'multiple',
        };
      }

      return {
        name: column.name,
        type: 'text',
      };
    case 'NUMBER':
      return {
        name: column.name,
        type: 'int',
      };
    case 'DATE':
    case 'DATETIME':
      return {
        name: column.name,
        type: 'date',
      };
    case 'YEAR':
      return {
        name: column.name,
        type: 'year',
      };
    case 'PRICE':
      return {
        name: column.name,
        type: 'text',
      };
    case 'BOOLEAN': {
      return {
        name: column.name,
        type: 'bool',
      };
    }
    default:
      assert.soft(
        false,
        `mapDataColumnsToSchema: Unexpected column type: ${column.type}`,
        column,
      );
      return {
        name: column.name,
        type: 'text',
      };
  }
};
